import { trpc } from '../../lib/trpc';
import { usePermissions, type PermissionKey } from '../../lib/permissions';
import { resolveSetupState, type SetupSnapshot } from '../../lib/setup-state';

export interface UseSetupStateResult {
  /** Null until the queries have resolved — never guess a state. */
  snapshot: SetupSnapshot | null;
  isPending: boolean;
  isError: boolean;
  error: { message: string } | null;
  refetch: () => void;
}

export interface UseSetupStateOptions {
  /**
   * Poll `status` while setup is unfinished. The corridor's verify step turns
   * this on so the "waiting for your reply" card flips by itself; the dashboard
   * banner leaves it off.
   */
  poll?: boolean;
}

/**
 * Feeds the two existing queries into {@link resolveSetupState}.
 *
 * Two things this deliberately does NOT do:
 *
 * 1. It never returns a snapshot while a query is still pending. Rendering
 *    NOT_CONNECTED for one frame to a vendor who is connected reads as "we lost
 *    your number" — a skeleton is strictly better than a wrong answer.
 * 2. It only fetches the manual config when a manual connection actually
 *    exists. That query is the sole source of `hasBYOCAppSecret`, but firing it
 *    for the 80% who connected through Embedded Signup would be a round-trip
 *    per page load for a field they can never need.
 */
export function useSetupState(opts: UseSetupStateOptions = {}): UseSetupStateResult {
  // Every query below is behind requirePermission('messaging'). The dashboard
  // banner renders for whoever is looking at the dashboard, so without this a
  // team member who only handles contacts would fire three guaranteed 403s on
  // every page load.
  const { can } = usePermissions();
  const enabled = can('messaging' as PermissionKey);

  const status = trpc.integrations.meta.status.useQuery(undefined, {
    enabled,
    // 3s matches how quickly a human replies to their own test message; the
    // corridor's verify step is the only caller that asks for it.
    refetchInterval: opts.poll ? 3000 : false,
  });
  const methods = trpc.integrations.meta.onboardingMethods.useQuery(undefined, { enabled });
  const persisted = trpc.integrations.meta.onboarding.getState.useQuery(undefined, { enabled });

  const hasManualNumber = (status.data?.whatsapp ?? []).some((w) => w.source === 'manual');
  const manual = trpc.integrations.meta.getWhatsAppManualConfig.useQuery(undefined, {
    enabled: enabled && hasManualNumber,
  });

  const utils = trpc.useUtils();
  const refetch = () => {
    void utils.integrations.meta.status.invalidate();
    void utils.integrations.meta.onboardingMethods.invalidate();
    void utils.integrations.meta.onboarding.getState.invalidate();
    if (hasManualNumber) void utils.integrations.meta.getWhatsAppManualConfig.invalidate();
  };

  // Not permitted is not the same as loading: return an inert, non-erroring
  // result so the banner simply renders nothing.
  if (!enabled) {
    return { snapshot: null, isPending: false, isError: false, error: null, refetch };
  }

  const isPending =
    status.isPending ||
    methods.isPending ||
    persisted.isPending ||
    (hasManualNumber && manual.isPending);
  const isError = status.isError || methods.isError || persisted.isError;
  const error = status.error ?? methods.error ?? persisted.error ?? null;

  if (isPending || isError || !status.data || !methods.data) {
    return { snapshot: null, isPending, isError, error, refetch };
  }

  return {
    snapshot: resolveSetupState({
      status: status.data,
      methods: methods.data,
      manual: manual.data ?? null,
      persisted: persisted.data ?? null,
    }),
    isPending: false,
    isError: false,
    error: null,
    refetch,
  };
}
